import type { ApplicationVersion, QuoteResults } from "@agency/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Button, Checkbox, Input, Select, Sheet } from "../components/ui";
import { useT } from "../i18n";

/** Creates application v1 from a small form, then requests quotes. */
export function StartQuoteSheet({ open, onClose, customerId, dob }: { open: boolean; onClose: () => void; customerId: string; dob?: string }) {
  const t = useT();
  const { config } = useConfig();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({
    effectiveDate: new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10),
    year: "2018",
    make: "Toyota",
    model: "Camry",
    vin: "",
    dob: dob ?? "",
    sr22: false,
    priorCarrier: "",
  });
  const m = useMutation({
    mutationFn: async () => {
      const app = await callAction<"agency.application.update", ApplicationVersion>("agency.application.update", {
        customerId,
        line: "auto",
        patch: {
          effectiveDate: form.effectiveDate,
          vehicles: [{ year: Number(form.year), make: form.make, model: form.model, vin: form.vin || undefined, use: "commute" }],
          drivers: form.dob ? [{ dob: form.dob, sr22: form.sr22 }] : [],
          priorCarrier: form.priorCarrier || undefined,
        },
      });
      const qr = await callAction<"agency.quote.request", QuoteResults>("agency.quote.request", { applicationId: app.result.id, applicationVersion: app.result.version });
      return qr;
    },
    onSuccess: (r) => {
      toast({ title: t("quote.requested"), tone: "success" });
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      onClose();
      navigate(`/quotes/${r.result.quoteRequest.id}`);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  const carriers = config.agency.carriers.filter((c) => c.lines.includes("auto"));
  return (
    <Sheet open={open} onClose={onClose} title={t("quote.start")}>
      <form onSubmit={submit} className="space-y-3">
        <Input label={t("quote.effectiveDate")} type="date" required value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
        <p className="text-sm font-bold text-ink/70 pt-1">{t("quote.vehicle")}</p>
        <div className="grid grid-cols-3 gap-2">
          <Input label={t("quote.year")} inputMode="numeric" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
          <Input label={t("quote.make")} value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
          <Input label={t("quote.model")} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
        </div>
        <Input label={t("quote.vin")} value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} placeholder="1HGCM82633A123456" />
        <Input label={t("quote.driverDob")} type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
        <Checkbox label={t("quote.sr22")} checked={form.sr22} onChange={(v) => setForm({ ...form, sr22: v })} />
        <Select label={t("quote.priorCarrier")} value={form.priorCarrier} onChange={(e) => setForm({ ...form, priorCarrier: e.target.value })}>
          <option value="">—</option>
          {carriers.map((c) => (
            <option key={c.name} value={c.name}>{c.name}</option>
          ))}
        </Select>
        <Button type="submit" size="lg" className="w-full" loading={m.isPending}>{t("quote.request")}</Button>
      </form>
    </Sheet>
  );
}
